import { oc } from "@orpc/contract";
import { z } from "zod";

export const RetentionModeSchema = z.enum([
  "dry-run",
  "archive-only",
  "archive-and-prune",
]);
export type RetentionMode = z.infer<typeof RetentionModeSchema>;

/** What the hot window counts for a given scope. */
export const HotWindowUnitSchema = z.enum(["runs", "days"]);
export type HotWindowUnit = z.infer<typeof HotWindowUnitSchema>;

/**
 * Whether these values came from a person on this page or from the process
 * environment the table replaced. The distinction is the page's whole point:
 * identical numbers mean different things depending on who chose them.
 */
export const SettingsSourceSchema = z.enum(["environment", "organization"]);
export type SettingsSource = z.infer<typeof SettingsSourceSchema>;

/**
 * When the archival job runs. Parts, not a cron string: this is an admin form,
 * and a picker can only produce valid values. UTC throughout, so a schedule
 * does not silently move twice a year for a job that deletes rows.
 */
export const ScheduleSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("manual") }),
  z.object({
    kind: z.literal("daily"),
    hourUtc: z.number().int().min(0).max(23),
  }),
  z.object({
    kind: z.literal("weekly"),
    hourUtc: z.number().int().min(0).max(23),
    /** 0 = Sunday. */
    dayOfWeek: z.number().int().min(0).max(6),
  }),
]);
export type Schedule = z.infer<typeof ScheduleSchema>;

export const RunStatusSchema = z.enum([
  "scheduled",
  "running",
  "completed",
  "failed",
  "cancelled",
  "interrupted",
]);
export type RunStatus = z.infer<typeof RunStatusSchema>;

export const DiagonalScalingRunSchema = z.object({
  id: z.string(),
  scope: z.string(),
  status: RunStatusSchema,
  triggerSource: z.enum(["manual", "schedule"]),
  mode: RetentionModeSchema.nullable(),
  scheduledFor: z.string(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  cancelRequestedAt: z.string().nullable(),
  summary: z
    .object({
      mode: z.string(),
      candidates: z.number(),
      archived: z.number(),
      pruned: z.number(),
      skipped: z.number(),
      failures: z.number(),
      rows: z.number().optional(),
      bytes: z.number().optional(),
    })
    .nullable(),
  error: z.string().nullable(),
  triggeredByUserId: z.string().nullable(),
  createdAt: z.string(),
});
export type DiagonalScalingRun = z.infer<typeof DiagonalScalingRunSchema>;

export const DiagonalScalingSettingsSchema = z.object({
  enabled: z.boolean(),
  mode: RetentionModeSchema,
  /** Null means unset, which blocks deletion. Not the same as zero. */
  keepHotUnits: z.number().int().positive().nullable(),
  maxAgeDays: z.number().int().positive().nullable(),
  batchSize: z.number().int().positive().max(100_000),
  /** Scope-specific extras; the backend validates against the scope's schema. */
  options: z.record(z.string(), z.unknown()),
  schedule: ScheduleSchema,
});
export type DiagonalScalingSettingsInput = z.infer<
  typeof DiagonalScalingSettingsSchema
>;

export const ResolvedDiagonalScalingSettingsSchema =
  DiagonalScalingSettingsSchema.extend({
    scope: z.string(),
    source: SettingsSourceSchema,
    /** The next occurrence the sweeper is waiting for, if any. */
    nextRunAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    updatedByUserId: z.string().nullable(),
    /** Why pruning is unavailable, when it is. Null means it is permitted. */
    deletionBlockedReason: z.string().nullable(),
  });
export type ResolvedDiagonalScalingSettings = z.infer<
  typeof ResolvedDiagonalScalingSettingsSchema
>;

export const DiagonalScalingScopeSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
  tables: z.array(z.string()),
  hotWindowUnit: HotWindowUnitSchema,
  keepHotLabel: z.string(),
  /** Whole-number options the page renders as inputs, keyed into `settings.options`. */
  numberOptions: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      description: z.string(),
      min: z.number().int(),
      max: z.number().int(),
    }),
  ),
  /** When this scope last archived anything for the org. Null means never. */
  lastArchivedAt: z.string().nullable(),
  /** How many units currently sit archived — evidence the job has run. */
  archivedCount: z.number(),
  /** A run queued or in flight right now, if there is one. */
  openRun: DiagonalScalingRunSchema.nullable(),
  /** The most recent run that finished, whatever its outcome. */
  lastRun: DiagonalScalingRunSchema.nullable(),
  settings: ResolvedDiagonalScalingSettingsSchema,
});
export type DiagonalScalingScope = z.infer<typeof DiagonalScalingScopeSchema>;

export const RepackStateSchema = z.object({
  status: z.enum(["running", "completed", "failed"]),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  error: z.string().nullable(),
});
export type RepackState = z.infer<typeof RepackStateSchema>;

export const LargeTableRowSchema = z.object({
  table: z.string(),
  /** Heap + indexes + TOAST. Null when the catalog has no entry. */
  totalBytes: z.number().nullable(),
  tableBytes: z.number().nullable(),
  indexBytes: z.number().nullable(),
  /** Planner estimate, never a count(*). Null before the first ANALYZE. */
  estimatedRows: z.number().nullable(),
  deadRows: z.number().nullable(),
  lastVacuumAt: z.string().nullable(),
  /** The diagonal-scaling scope that governs it, if any. */
  scope: z.string().nullable(),
  /** The latest compaction started from this page, if any. */
  repack: RepackStateSchema.nullable(),
});
export type LargeTableRow = z.infer<typeof LargeTableRowSchema>;

export const diagonalScalingContracts = {
  getDiagonalScaling: oc
    .input(
      z.object({
        /** How many of the biggest tables to return. */
        tableLimit: z.number().int().positive().max(200).default(25),
      }),
    )
    .output(
      z.object({
        organizationId: z.string(),
        scopes: z.array(DiagonalScalingScopeSchema),
        tables: z.array(LargeTableRowSchema),
      }),
    )
    .route({
      summary: "Get data archival settings and table sizes",
      description:
        "Admin only. Returns the organization's data archival scopes with settings, schedule, open and last runs, plus the largest database tables by size.",
      method: "POST",
    }),

  runDiagonalScalingNow: oc
    .input(z.object({ scope: z.string() }))
    .output(DiagonalScalingRunSchema)
    .route({
      summary: "Run data archival now",
      description:
        "Admin only. Queues an archival run for one scope to start on the next sweep, archiving old records and pruning them if the scope's mode allows.",
      method: "POST",
    }),

  cancelDiagonalScalingRun: oc
    .input(z.object({ runId: z.string() }))
    .output(
      z.object({
        /** `requested` for a running job: its process records the outcome. */
        result: z.enum(["cancelled", "requested", "not-open"]),
      }),
    )
    .route({
      summary: "Cancel a data archival run",
      description:
        "Admin only. Cancels a queued archival run, or requests a running one to stop; scoped to the caller's organization.",
      method: "POST",
    }),

  listDiagonalScalingRuns: oc
    .input(
      z.object({
        scope: z.string().optional(),
        page: z.number().int().positive().default(1),
        pageSize: z.number().int().positive().max(100).default(20),
      }),
    )
    .output(
      z.object({
        items: z.array(DiagonalScalingRunSchema),
        total: z.number(),
      }),
    )
    .route({
      summary: "List data archival run history",
      description:
        "Admin only. Paged archival runs for the organization, optionally by scope, with mode, archived and pruned counts, timing and errors.",
      method: "POST",
    }),

  repackLargeTable: oc
    .input(z.object({ table: z.string() }))
    .output(RepackStateSchema)
    .route({
      summary: "Compact a large table",
      description:
        "Admin only. Starts an online pg_repack of one archival-governed table to return the space archival freed to the filesystem. Needs free disk of about the table's live size.",
      method: "POST",
    }),

  updateDiagonalScalingSettings: oc
    .input(
      z.object({
        scope: z.string(),
        settings: DiagonalScalingSettingsSchema,
      }),
    )
    .output(ResolvedDiagonalScalingSettingsSchema)
    .route({
      summary: "Update data archival settings",
      description:
        "Admin only. Saves one scope's archival settings: enabled, dry-run/archive/prune mode, hot window, max age, batch size and schedule.",
      method: "POST",
    }),
};
